/**
 * Privacy & Anonymity Utilities
 * 
 * Core privacy principles enforced:
 * 1. Zero metadata collection
 * 2. No IP/device fingerprinting
 * 3. No timestamps stored after delivery
 * 4. Client-side encryption only
 * 5. Tor/VPN friendly
 */

// Privacy-safe random ID generator (no timestamps or identifiable data)
export function generateSecureId(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

// Strip all metadata from objects before storage/transmission
export function stripMetadata<T extends Record<string, unknown>>(obj: T, keysToRemove: string[] = []): Partial<T> {
  const dangerousKeys = [
    'ip', 'ipAddress', 'userAgent', 'deviceId', 'fingerprint',
    'location', 'latitude', 'longitude', 'timezone', 'language',
    'referrer', 'origin', 'hostname', 'platform', 'vendor',
    ...keysToRemove
  ];
  
  const stripped: Partial<T> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (!dangerousKeys.includes(key.toLowerCase())) {
      stripped[key as keyof T] = value as T[keyof T];
    }
  }
  return stripped;
}

// Secure memory wipe for sensitive data
export function secureWipe(data: string | Uint8Array): void {
  if (typeof data === 'string') {
    // Overwrite string reference (best effort in JS)
    const length = data.length;
    for (let i = 0; i < length; i++) {
      // This doesn't actually work in JS due to string immutability
      // but we try our best - real solution is using Uint8Array
    }
  } else if (data instanceof Uint8Array) {
    crypto.getRandomValues(new Uint8Array(data.buffer as ArrayBuffer));
    data.fill(0);
  }
}

// Privacy-safe console logging (strips sensitive data)
const originalConsole = {
  log: console.log,
  warn: console.warn,
  error: console.error,
  info: console.info,
};

const sensitivePatterns = [
  /passphrase/i,
  /password/i,
  /secret/i,
  /private.*key/i,
  /mnemonic/i,
  /seed/i,
  /token/i,
];

function sanitizeLogArg(arg: unknown): unknown {
  if (typeof arg === 'string') {
    for (const pattern of sensitivePatterns) {
      if (pattern.test(arg)) {
        return '[REDACTED]';
      }
    }
  }
  if (typeof arg === 'object' && arg !== null) {
    const sanitized: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(arg)) {
      if (sensitivePatterns.some(p => p.test(key))) {
        sanitized[key] = '[REDACTED]';
      } else {
        sanitized[key] = sanitizeLogArg(value);
      }
    }
    return sanitized;
  }
  return arg;
}

export function enablePrivacySafeLogging(): void {
  console.log = (...args) => originalConsole.log(...args.map(sanitizeLogArg));
  console.warn = (...args) => originalConsole.warn(...args.map(sanitizeLogArg));
  console.error = (...args) => originalConsole.error(...args.map(sanitizeLogArg));
  console.info = (...args) => originalConsole.info(...args.map(sanitizeLogArg));
}

// Disable all console logging in production
export function disableLoggingInProduction(): void {
  if (import.meta.env.PROD) {
    console.log = () => {};
    console.warn = () => {};
    console.error = () => {};
    console.info = () => {};
    console.debug = () => {};
    console.trace = () => {};
  }
}

// Check if running through Tor or privacy tools
export function detectPrivacyMode(): {
  isTor: boolean;
  isPrivateBrowsing: boolean;
  hasReducedFingerprint: boolean;
} {
  const isTor = navigator.userAgent.includes('Tor') || 
    window.innerWidth === 1000 && window.innerHeight === 900; // Common Tor dimensions
  
  let isPrivateBrowsing = false;
  try {
    localStorage.setItem('__privacy_test__', '1');
    localStorage.removeItem('__privacy_test__');
  } catch {
    isPrivateBrowsing = true;
  }
  
  const hasReducedFingerprint = !navigator.plugins?.length || 
    navigator.hardwareConcurrency === undefined;
  
  return { isTor, isPrivateBrowsing, hasReducedFingerprint };
}

// Privacy headers to request from server
export const PRIVACY_HEADERS = {
  // Don't track
  'DNT': '1',
  // Global Privacy Control
  'Sec-GPC': '1',
  // No referrer
  'Referrer-Policy': 'no-referrer',
};

// Time obfuscation - add random delay to prevent timing attacks
export async function obfuscatedDelay(minMs: number = 50, maxMs: number = 200): Promise<void> {
  const delay = Math.floor(Math.random() * (maxMs - minMs + 1)) + minMs;
  return new Promise(resolve => setTimeout(resolve, delay));
}

// Secure session timeout manager
export class PrivacySessionManager {
  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly timeoutMs: number;
  private readonly onTimeout: () => void;
  private lastActivity: number = Date.now();

  constructor(timeoutMs: number = 5 * 60 * 1000, onTimeout: () => void) {
    this.timeoutMs = timeoutMs;
    this.onTimeout = onTimeout;
    this.startMonitoring();
  }

  private startMonitoring(): void {
    this.resetTimer();
    
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(event => {
      window.addEventListener(event, this.handleActivity, { passive: true });
    });
    
    // Monitor visibility changes
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  private handleActivity = (): void => {
    this.lastActivity = Date.now();
    this.resetTimer();
  };

  private handleVisibilityChange = (): void => {
    if (document.hidden) {
      // When tab becomes hidden, use shorter timeout
      this.resetTimer(60000); // 1 minute when hidden
    } else {
      this.resetTimer();
    }
  };

  private resetTimer(customTimeout?: number): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.timeoutId = setTimeout(this.onTimeout, customTimeout ?? this.timeoutMs);
  }

  public destroy(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(event => {
      window.removeEventListener(event, this.handleActivity);
    });
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  public getInactiveTime(): number {
    return Date.now() - this.lastActivity;
  }
}

// Prevent clipboard snooping
export function secureClipboardCopy(text: string, clearAfterMs: number = 30000): void {
  if (!document.hasFocus()) return;
  navigator.clipboard.writeText(text).then(() => {
    setTimeout(() => {
      if (document.hasFocus()) navigator.clipboard.writeText('').catch(() => {});
    }, clearAfterMs);
  }).catch(() => {});
}

// Privacy audit - check for potential leaks
export function runPrivacyAudit(): {
  issues: string[];
  warnings: string[];
  score: number;
} {
  const issues: string[] = [];
  const warnings: string[] = [];
  
  // Check for localStorage sensitive data exposure
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && sensitivePatterns.some(p => p.test(key))) {
        warnings.push(`Potentially sensitive localStorage key: ${key}`);
      }
    }
  } catch {
    // localStorage not available
  }
  
  // Check for service workers (could cache sensitive data)
  if ('serviceWorker' in navigator) {
    warnings.push('Service workers enabled - ensure no sensitive data is cached');
  }
  
  // Check for IndexedDB (could store sensitive data)
  if ('indexedDB' in window) {
    warnings.push('IndexedDB available - ensure proper encryption of stored data');
  }
  
  // Check console overrides
  if (console.log === originalConsole.log) {
    issues.push('Console logging not sanitized - enable privacy-safe logging');
  }
  
  const score = Math.max(0, 100 - (issues.length * 20) - (warnings.length * 5));
  
  return { issues, warnings, score };
}
